import { useState, useEffect } from 'react'
import { getConteudoChave, putConteudoChave, CONTEUDO_CHAVES } from '@/api/adminApi'
import { AdminPage, AdminPanel } from '@/components/Admin'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Admin.module.css'

export type CategoriaItem = { nome: string }

const CHAVE = CONTEUDO_CHAVES.categorias

function parseCategorias(raw: string | null): CategoriaItem[] {
  if (!raw?.trim()) return []
  try {
    const arr = JSON.parse(raw) as unknown
    if (!Array.isArray(arr)) return []
    return arr
      .filter((x): x is { nome?: string } => x != null && typeof x === 'object')
      .map((x) => ({ nome: String(x.nome ?? '').trim() }))
      .filter((x) => x.nome)
  } catch {
    return []
  }
}

export function AdminCategoriasPage() {
  const [items, setItems] = useState<CategoriaItem[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { notifySaved, notifyError } = useSaveFeedback()

  useEffect(() => {
    let cancelled = false
    setError(null)
    getConteudoChave(CHAVE)
      .then((v) => {
        if (cancelled) return
        setItems(parseCategorias(v ?? null))
      })
      .catch(() => {
        if (!cancelled) setItems([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const updateItem = (index: number, value: string) => {
    setItems((prev) => {
      const next = [...prev]
      if (!next[index]) next[index] = { nome: '' }
      next[index] = { nome: value }
      return next
    })
  }

  const addItem = () => {
    setItems((prev) => [...prev, { nome: '' }])
  }

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    const filtradas = items.filter((x) => x.nome.trim())
    try {
      await putConteudoChave(CHAVE, JSON.stringify(filtradas))
      setItems(filtradas)
      notifySaved('Categorias guardadas.')
    } catch (e) {
      notifyError(e, 'Erro ao salvar')
      setError(e instanceof Error ? e.message : 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <AdminPage
      title="Categorias"
      subtitle="Nome de cada categoria. Aparecem na página de Serviços do app para o cliente filtrar."
      loading={loading}
      error={error}
    >
      <form onSubmit={handleSalvar} className={styles.adminFormStack}>
        <AdminPanel
          title={
            <>
              Lista de categorias
              {items.length > 0 && (
                <span className={styles.listCountBadge}>{items.length}</span>
              )}
            </>
          }
          description="Ex.: Cabelo, Barba, Unhas — uma categoria por linha."
          footer={
            <button type="submit" className={styles.btnSalvar} disabled={saving}>
              {saving ? 'A guardar…' : 'Guardar categorias'}
            </button>
          }
        >
          {items.length === 0 && (
            <p className={styles.listEmpty}>Nenhuma categoria. Adicione abaixo.</p>
          )}
          {items.length > 0 && (
            <div className={styles.listEditorScroll}>
              <div
                className={`${styles.listEditorHead} ${styles['listEditorHead--categoriaNome']}`}
                aria-hidden
              >
                <span>#</span>
                <span>Nome</span>
                <span />
              </div>
              {items.map((item, i) => (
                <div key={i} className={`${styles.vipListItem} ${styles['vipListItem--categoriaNome']}`}>
                  <span className={styles.rowIndex}>{i + 1}</span>
                  <input
                    type="text"
                    value={item.nome}
                    onChange={(e) => updateItem(i, e.target.value)}
                    placeholder="Nome da categoria"
                    aria-label={`Nome da categoria ${i + 1}`}
                  />
                  <button
                    type="button"
                    className={styles.vipBtnRemove}
                    onClick={() => removeItem(i)}
                    aria-label="Remover categoria"
                  >
                    Remover
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className={styles.vipListAdd}>
            <button type="button" className={styles.vipBtnAdd} onClick={addItem}>
              + Adicionar categoria
            </button>
          </div>
        </AdminPanel>
      </form>
    </AdminPage>
  )
}
